// web/src/components/Turnstile.tsx — Cloudflare Turnstile widget (portal spec 24). Loads Cloudflare's script once, renders
// explicitly with the public site key (VITE_TURNSTILE_SITE_KEY) and calls onToken when solved ('' when it expires or
// errors). Tokens are single-use: bump `resetSignal` after a failed send to get a fresh one.
// Mock mode without a key hands back a fake token; a real build without a key shows a short note instead (no token).
import { useEffect, useRef } from 'react';
import { IS_MOCK } from '../lib/api';

interface TurnstileApi {
  render: (el: HTMLElement, o: Record<string, unknown>) => string;
  reset: (id?: string) => void;
  remove: (id?: string) => void;
}
declare global { interface Window { turnstile?: TurnstileApi } }

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';
/** Must match the API's TURNSTILE_ACTION (api/src/modules/preview/turnstile.service.ts). */
const ACTION = 'preview-form';
let loading: Promise<void> | null = null;
function load(): Promise<void> {
  loading ??= new Promise<void>((res, rej) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.onload = () => res();
    s.onerror = () => { loading = null; rej(new Error('turnstile script failed to load')); };
    document.head.appendChild(s);
  });
  return loading;
}

export const turnstileAvailable = !!SITE_KEY || IS_MOCK;

export function Turnstile({ onToken, resetSignal = 0 }: { onToken: (t: string) => void; resetSignal?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const widget = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!SITE_KEY) { if (IS_MOCK) onToken('mock-turnstile-token'); return; }
    let gone = false;
    load().then(() => {
      if (gone || !ref.current || !window.turnstile) return;
      widget.current = window.turnstile.render(ref.current, {
        sitekey: SITE_KEY, action: ACTION, theme: 'auto', size: 'flexible',
        callback: onToken, 'expired-callback': () => onToken(''), 'error-callback': () => onToken(''),
      });
    }).catch(() => onToken(''));
    return () => { gone = true; if (widget.current) window.turnstile?.remove(widget.current); widget.current = undefined; };
  }, [onToken]);

  useEffect(() => {
    if (!resetSignal) return;
    if (widget.current) { onToken(''); window.turnstile?.reset(widget.current); }
    else if (!SITE_KEY && IS_MOCK) onToken('mock-turnstile-token');
  }, [resetSignal, onToken]);

  if (!SITE_KEY && !IS_MOCK) {
    return <p className="rounded-md border border-hair bg-surface px-3 py-2 text-sm text-muted">The form isn’t switched on yet. Please check back soon.</p>;
  }
  return <div ref={ref} className="min-h-16 w-full max-w-full overflow-hidden" />;
}
